'use client';

import type { SelectedDate } from '@yanolja-rnd/nol-design-system';
import type { FC } from 'react';
import type { DeviceType } from '@weberus/shared-types';
import type {
  RoomCapacity,
  CapacitySelectProps,
} from '@/app/_components/common/date-capacity-select-bottom-sheet/capacity-select';
import type { DateSelectProps } from '@/app/_components/common/date-capacity-select-bottom-sheet/date-select';
import { useState, useMemo, useEffect } from 'react';
import { BottomSheet, cn, RectangleButton } from '@yanolja-rnd/nol-design-system';
import { addDays } from 'date-fns';
import { DateSelect } from '@/app/_components/common/date-capacity-select-bottom-sheet/date-select';
import {
  CapacitySelect,
  DEFAULT_CAPACITY_ROOMS,
} from '@/app/_components/common/date-capacity-select-bottom-sheet/capacity-select';

export type DateCapacityOpenType = 'date' | 'capacity';

export interface DateCapacitySelectBottomSheetProps {
  isOpen?: boolean;
  deviceType?: DeviceType;
  openType?: DateCapacityOpenType;
  date?: Pick<DateSelectProps, 'selectedDate' | 'noticeText' | 'description' | 'maxDuration'>;
  capacity?: Pick<CapacitySelectProps, 'rooms' | 'adultCount' | 'childrenCount'>;
  /** 해외 숙소는 객실까지 선택 가능함 */
  isGlobal?: boolean;
  onApply: ({ date, rooms }: { date?: SelectedDate; rooms?: RoomCapacity[] }) => void;
  onClose: () => void;
  /** 날짜 선택 모달이 열릴 때 호출되는 콜백 함수 */
  onDateModalOpen?: () => void;
  /** 인원 선택 모달이 열릴 때 호출되는 콜백 함수 */
  onCapacityModalOpen?: () => void;
}

const DEFAULT_MAX_DURATION = 9;

export const DateCapacitySelectBottomSheet: FC<DateCapacitySelectBottomSheetProps> = (props) => {
  const {
    isOpen = false,
    openType,
    deviceType,
    date,
    capacity,
    isGlobal,
    onApply,
    onClose,
    onDateModalOpen,
    onCapacityModalOpen,
  } = props;
  const { selectedDate, noticeText, description, maxDuration = DEFAULT_MAX_DURATION } = date ?? {};
  const { rooms } = capacity ?? {};
  const isPc = deviceType === 'etc';

  const [rawSelectedDate, setRawSelectedDate] = useState<SelectedDate | undefined>(selectedDate);
  const [rawSelectedRooms, setRawSelectedRooms] = useState<RoomCapacity[]>(rooms || DEFAULT_CAPACITY_ROOMS);
  const [selectOpenType, setSelectOpenType] = useState<DateCapacityOpenType | undefined>(openType);
  const [isScrolling, setIsScrolling] = useState(false);

  const isDiabledApplyButton = useMemo(() => {
    // 첫번째 날짜가 체크 되어 있지 않으면 적용 불가
    if (!rawSelectedDate?.[0] || !selectedDate?.[0]) return false;

    // TODO: 고속버스 날짜 선택 시 disabled 처리 필요
  }, [rawSelectedDate, selectedDate]);

  const handledateCapacitySelectApply = () => {
    if (!rawSelectedDate?.[0]) return;

    // 체크인만 설정 되어 있는 경우: 체크아웃을 하루 뒤로 자동 선택
    const checkInDate = rawSelectedDate[0];
    const checkOutDate = rawSelectedDate[1] ?? addDays(checkInDate, 1);

    const autoSelectedDate: SelectedDate = [checkInDate, checkOutDate];

    setRawSelectedDate(autoSelectedDate);
    onApply({ date: autoSelectedDate, rooms: rawSelectedRooms });
  };

  useEffect(() => {
    // BottomSheet open 시, state 동기화
    if (!isOpen) return;

    setRawSelectedRooms(rooms || DEFAULT_CAPACITY_ROOMS);
    setRawSelectedDate(selectedDate);
  }, [isOpen, rooms, selectedDate]);

  useEffect(() => {
    // props.openType 변경 시, state 동기화
    setSelectOpenType(openType);
  }, [isOpen, openType]);

  return (
    <BottomSheet
      header="날짜, 인원 선택"
      os={deviceType}
      isOpen={isOpen}
      height="full"
      zIndex={40}
      contentDisableDrag={isScrolling}
      footer={
        <RectangleButton
          disabled={isDiabledApplyButton}
          size="large"
          weight="bold"
          fullWidth
          onClick={handledateCapacitySelectApply}>
          적용하기
        </RectangleButton>
      }
      dialogOptions={{
        className: 'w-[600px]',
        height: 'full',
      }}
      onClose={onClose}>
      <div className={cn('flex h-full flex-col gap-16 pb-32 pt-10', { 'px-16': !isPc })}>
        <DateSelect
          isGlobal={isGlobal}
          isOpen={selectOpenType === 'date'}
          deviceType={deviceType}
          selectedDate={rawSelectedDate}
          noticeText={noticeText}
          description={description}
          maxDuration={maxDuration}
          onDateChange={setRawSelectedDate}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              return;
            }

            onDateModalOpen?.();
            setSelectOpenType('date');
          }}
        />
        <CapacitySelect
          isOpen={selectOpenType === 'capacity'}
          deviceType={deviceType}
          rooms={rawSelectedRooms}
          isGlobal={isGlobal}
          onScrollingChange={(value) => {
            setIsScrolling(value);
          }}
          onCapacityChange={(newRooms) => {
            setRawSelectedRooms(newRooms);
          }}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              return;
            }

            onCapacityModalOpen?.();
            setSelectOpenType('capacity');
          }}
        />
      </div>
    </BottomSheet>
  );
};
